import { EventRoleTemplate } from "@/types/EventRoleTemplate";
import { EventRoleTemplateCreationDTO } from "@/types/EventRoleTemplateCreationDTO";
import { apiDelete, apiGet, apiPost, apiPut } from "../_apiShim";

const BASE_PATH = "/api/v1/event-role-templates/";

export async function listEventRoleTemplates(
  eventId: string,
  fields?: string,
): Promise<EventRoleTemplate[]> {
  const qp = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  return await apiGet<EventRoleTemplate[]>(
    `${BASE_PATH}${eventId}${qp}`,
    { "Cache-Control": "no-store" },
    true,
  );
}

export async function getEventRoleTemplate(
  eventId: string,
  templateId: string,
  fields?: string,
  /** Ride the admin's own auth instead of an active preview bearer (the preview
   *  role may lack `event-role-templates:read`). See client.ts `X-Preview-Bypass`. */
  bypassPreview = false,
): Promise<EventRoleTemplate> {
  const qp = fields ? `?fields=${encodeURIComponent(fields)}` : "";
  const headers: Record<string, string> = { "Cache-Control": "no-store" };
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  return await apiGet<EventRoleTemplate>(
    `${BASE_PATH}${eventId}/${templateId}${qp}`,
    headers,
    true,
  );
}

export async function createEventRoleTemplate(
  eventId: string,
  body: EventRoleTemplateCreationDTO,
): Promise<EventRoleTemplate> {
  return await apiPost<EventRoleTemplate>(
    `${BASE_PATH}${eventId}`,
    body,
    {},
    true,
  );
}

/**
 * The `If-Match` header for a role-template PUT (B2): the version the editor
 * was seeded with (`version ?? 0`). The backend refuses a PUT to an "All
 * except" template without one (400 `ifMatchRequired`) and a stale one with
 * 409 `versionMismatch`. A value that is no version is a caller bug and
 * throws: silently dropping the precondition would turn the save into a
 * last-write-wins.
 */
export function roleTemplateIfMatchHeader(ifMatch: number | undefined): Record<string, string> {
  if (ifMatch === undefined) return {};
  if (!Number.isSafeInteger(ifMatch) || ifMatch < 0) throw new Error("validationError");
  return { "If-Match": String(ifMatch) };
}

export async function updateEventRoleTemplate(
  eventId: string,
  templateId: string,
  body: EventRoleTemplateCreationDTO,
  /** Ride the admin's own auth instead of an active preview bearer (the preview
   *  is read-only and would 403 this write). See client.ts `X-Preview-Bypass`. */
  bypassPreview = false,
  /** `ifMatch`: the seeded `version ?? 0`, sent as `If-Match` (B2). Optional for
   *  now; every writer passes it once FE-3 lands. */
  opts: { ifMatch?: number } = {},
): Promise<EventRoleTemplate> {
  const headers: Record<string, string> = { ...roleTemplateIfMatchHeader(opts.ifMatch) };
  if (bypassPreview) headers["X-Preview-Bypass"] = "1";
  return await apiPut<EventRoleTemplate>(
    `${BASE_PATH}${eventId}/${templateId}`,
    body,
    headers,
    true,
  );
}

export async function deleteEventRoleTemplate(
  eventId: string,
  templateId: string,
): Promise<void> {
  await apiDelete(`${BASE_PATH}${eventId}/${templateId}`, {}, true);
}
